import React from 'react';

import BufferOptionsItem from 'buffers/BufferOptionsItem';
import CreatePDFButton from 'molbio/maps/options/CreatePDFButton';
import HiddenAnnotationsWarningBadge from 'molbio/maps/options/HiddenAnnotationsWarningBadge';
import HiddenCutsitesWarningBadge from 'molbio/maps/options/HiddenCutsitesWarningBadge';
import {MiniMapSequenceOptionsProps} from 'molbio/maps/options/MiniMapSequenceOptionsView';
import SearchButton from 'molbio/maps/options/SearchButton';
import SequenceOptionsBanner from 'molbio/maps/options/SequenceOptionsBanner';
import {ZoomSlider} from 'molbio/maps/options/Sliders';
import useCollapsibleNavBar from 'molbio/maps/options/useCollapsibleNavBar';
import {shouldShowHiddenAnnotationsWarning, shouldShowHiddenCutsitesWarning} from 'molbio/maps/options/utils';
import DisplayOptions from 'settings/visibility-options/DisplayOptions';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

enum ElementKey {
  PDF = 'pdf',
  SEARCH = 'search',
  ANNOTATION_WARNING = 'annotation_warning',
  CUTSITES_WARNING = 'cutsites_warning',
}

function MiniMapOptions({
  bannerText,
  onOpenPreview,
  onToggleSearch,
  visibilityOptions,
  featureCounts,
  zoomSliderProps,
  onHiddenCutsitesLinkClick,
  cutsiteFilter,
  shownAnnotationsCount,
  visibleAnnotationsCount,
  sequenceId,
}: MiniMapSequenceOptionsProps): JSX.Element {
  let uncollapsibleWidth = 148; // Zoom slider (110) + visibility options (38)
  const showAnnotationsWarning = shouldShowHiddenAnnotationsWarning({
    shownAnnotationsCount,
    visibleAnnotationsCount,
  });
  const showCutsiteWarning = shouldShowHiddenCutsitesWarning({cutsiteFilter});
  if (showAnnotationsWarning) {
    uncollapsibleWidth += 36; // Icon of warning badge
  }
  if (showCutsiteWarning) {
    uncollapsibleWidth += 36; // Icon of warning badge
  }
  if (bannerText != null) {
    uncollapsibleWidth += 10; // The banner text collapses, but its margin does not
  }

  const {ref, hiddenStatusByElementKey} = useCollapsibleNavBar<ElementKey>({
    uncollapsibleWidth,
    dropdownWidth: 24,
    collapsibleElements: [
      {key: ElementKey.PDF, width: 92, priority: 2},
      {key: ElementKey.SEARCH, width: 24, priority: 1},
      ...(showAnnotationsWarning
        ? [
            {
              key: ElementKey.ANNOTATION_WARNING,
              width: 69, // Width of badge with text (105) - badge without text (36)
              priority: 3,
            },
          ]
        : []),
      ...(showCutsiteWarning
        ? [
            {
              key: ElementKey.CUTSITES_WARNING,
              width: 89, // Width of badge with text (125) - badge without text (36)
              priority: 4,
            },
          ]
        : []),
    ],
  });

  const collapsedButtonMenuItems = [
    <Option onSelect={onOpenPreview} key={ElementKey.PDF}>
      Create PDF
    </Option>,
    <Option onSelect={onToggleSearch} key={ElementKey.SEARCH}>
      Search
    </Option>,
  ].filter((item) => hiddenStatusByElementKey[item.key as ElementKey]);

  return (
    <div className="buffer-options-container" ref={ref}>
      <BufferOptionsItem>
        <ZoomSlider {...zoomSliderProps} />
      </BufferOptionsItem>
      {showAnnotationsWarning && (
        <HiddenAnnotationsWarningBadge iconOnly={hiddenStatusByElementKey[ElementKey.ANNOTATION_WARNING]} />
      )}
      {showCutsiteWarning && (
        <HiddenCutsitesWarningBadge
          // showCutsiteWarning filters out null cutsites
          cutsiteFilter={cutsiteFilter!}
          onHiddenCutsitesLinkClick={onHiddenCutsitesLinkClick}
          iconOnly={hiddenStatusByElementKey[ElementKey.CUTSITES_WARNING]}
        />
      )}
      <SequenceOptionsBanner overrideBannerText={bannerText} sequenceId={sequenceId} />
      <BufferOptionsItem className="u-vertical-center-items">
        <ButtonGroup>
          {!hiddenStatusByElementKey[ElementKey.PDF] && <CreatePDFButton onOpenPreview={onOpenPreview} />}
        </ButtonGroup>
        <ButtonGroup>
          {collapsedButtonMenuItems.length > 0 && (
            <Dropdown menu={<Menu width={MenuWidth.SMALL}>{collapsedButtonMenuItems}</Menu>}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.MINI}
                isDropdown={true}
                hideDropdownCaret={true}
                icon="ellipsis-vertical"
              />
            </Dropdown>
          )}
        </ButtonGroup>
        <ButtonGroup>
          <DisplayOptions dropLeft={true} options={visibilityOptions} featureCounts={featureCounts} />
          {!hiddenStatusByElementKey[ElementKey.SEARCH] && <SearchButton onToggleSearch={onToggleSearch} />}
        </ButtonGroup>
      </BufferOptionsItem>
    </div>
  );
}

export default MiniMapOptions;
